/**
 * Belt color definitions that match the original Marune app
 * Includes all 9 colors from the original app's /belts/supportedValues/colors
 */
import logger from './logger';

/**
 * All 9 belt colors supported by the original Marune app
 * Matches Firebase /belts/supportedValues/colors exactly
 */
export const BELT_COLORS: Record<string, {
  name: string;
  backgroundColorCode: string;
  textColorCode: string;
  stripeBackgroundColorCode: string;
  cssClass: string;
}> = {
  white: {
    name: 'white',
    backgroundColorCode: '#ffffff',
    textColorCode: '#222222',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-white border border-gray-300 dark:border-gray-600'
  },
  yellow: {
    name: 'yellow',
    backgroundColorCode: '#FED83A',
    textColorCode: '#222222',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-yellow-400'
  },
  orange: {
    name: 'orange',
    backgroundColorCode: '#FF7728',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-orange-500'
  },
  green: {
    name: 'green',
    backgroundColorCode: '#289234',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-green-500'
  },
  blue: {
    name: 'blue',
    backgroundColorCode: '#0012B0',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-blue-600'
  },
  red: {
    name: 'red',
    backgroundColorCode: '#FF0011',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-red-600'
  },
  purple: {
    name: 'purple',
    backgroundColorCode: '#9B00FF',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-purple-600'
  },
  brown: {
    name: 'brown',
    backgroundColorCode: '#924D20',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-amber-700'
  },
  black: {
    name: 'black',
    backgroundColorCode: '#000000',
    textColorCode: '#FFFFFF',
    stripeBackgroundColorCode: '#ff0000', // Red for black belt stripes
    cssClass: 'bg-black'
  },
  grey: {
    name: 'grey',
    backgroundColorCode: '#CCCCCC',
    textColorCode: '#222222',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-gray-400'
  },
  none: {
    name: 'none',
    backgroundColorCode: 'transparent',
    textColorCode: '#222222',
    stripeBackgroundColorCode: '#000000',
    cssClass: 'bg-transparent'
  }
};

/**
 * Contrast types for belt patterns (from original app)
 * Used for special belts like coral belt (black/red alternating)
 */
export const CONTRAST_TYPES = [
  'none',                         // No contrast/pattern (solid color)
  'alternate',                    // Alternating color sections (e.g., coral belt)
  'centered horizontal stripe',   // Single stripe through center
  'top horizontal stripe',        // Stripe at top of belt
  '2x horizontal stripes'         // Double horizontal stripes
] as const;

export type ContrastType = typeof CONTRAST_TYPES[number];

/**
 * Gets the CSS class for a belt color
 * @param beltRank The belt rank/color
 * @returns The corresponding CSS class
 */
export function getBeltColorClass(beltRank: string): string {
  if (!beltRank) {
    logger.info('getBeltColorClass received undefined or empty beltRank');
    return BELT_COLORS.white.cssClass;
  }
  
  // Normalize the input to lowercase
  const normalizedRank = beltRank.toLowerCase();
  
  // Exact match check
  if (normalizedRank in BELT_COLORS) {
    logger.info(`Found exact match for belt color: ${normalizedRank}`);
    return BELT_COLORS[normalizedRank as keyof typeof BELT_COLORS].cssClass;
  }
  
  // Partial match check for belt names
  for (const [color, data] of Object.entries(BELT_COLORS)) {
    if (normalizedRank.includes(color)) {
      logger.info(`Found partial match for belt color: ${color} in ${normalizedRank}`);
      return data.cssClass;
    }
  }
  
  // If it's a hex color, return a direct bg color style
  if (normalizedRank.startsWith('#')) {
    logger.info(`Using hex color: ${normalizedRank}`);
    return `bg-[${normalizedRank}]`;
  }
  
  // Handle special cases with color names in the rank
  if (normalizedRank.includes('purple')) return BELT_COLORS.purple.cssClass;
  if (normalizedRank.includes('blue')) return BELT_COLORS.blue.cssClass;
  if (normalizedRank.includes('brown')) return BELT_COLORS.brown.cssClass;
  if (normalizedRank.includes('black')) return BELT_COLORS.black.cssClass;
  if (normalizedRank.includes('white')) return BELT_COLORS.white.cssClass;
  
  // Fallback to white belt with logging
  logger.info(`No match found for belt rank: ${normalizedRank}, using default white`);
  return BELT_COLORS.white.cssClass;
}